/* ==========================================================================
   SAUVRYN — CART + QUICK VIEW  (global)
   ==========================================================================
   Real Shopify cart. One delegated controller handles:
     - the bag drawer (open/close, qty +/-, remove, checkout) with live
       re-render via the Section Rendering API (section id "sauvryn-bag"),
     - the quick-view modal (lazy-wired, works on ANY [data-qa] panel: home
       cards, PDP "you may also like", and bag recommendations),
     - a shared add-to-cart that refreshes the bag and opens it.
   Strings come from window.sauvrynStrings (set in layout/theme.liquid).
   ========================================================================== */
(() => {
  const S = window.sauvrynStrings || {};
  const html = document.documentElement;
  const SECTIONS = 'sauvryn-bag,cart-icon-bubble';

  const cssEsc = (s) => (window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/"/g, '\\"'));

  /* ---------- Section Rendering API swaps ---------- */
  const applyBubble = (h) => {
    if (!h) return;
    const b = document.getElementById('cart-icon-bubble');
    if (!b) return;
    const t = document.createElement('div');
    t.innerHTML = h;
    const src = t.querySelector('#cart-icon-bubble') || t;
    b.innerHTML = src.innerHTML;
  };
  const refreshBag = (data) => {
    if (!data || !data.sections) return;
    applyBubble(data.sections['cart-icon-bubble']);
    const bagHtml = data.sections['sauvryn-bag'];
    if (bagHtml) {
      const doc = new DOMParser().parseFromString(bagHtml, 'text/html');
      ['[data-bag-items]', '[data-bag-foot]', '[data-bag-title]'].forEach((sel) => {
        const nw = doc.querySelector(sel);
        const cur = document.querySelector(sel);
        if (nw && cur) cur.innerHTML = nw.innerHTML;
      });
    }
    paintShipBar();
  };

  /* ---------- free-delivery progress bar ----------
     The foot is re-rendered whole on every cart op, so a fresh fill would snap
     to its final width with no transition. We stash the last percentage on the
     persistent [data-bag-foot] container and animate previous -> new so the bar
     glides (0.3s in CSS) as the subtotal changes. */
  const paintShipBar = () => {
    const foot = document.querySelector('[data-bag-foot]');
    const fill = document.querySelector('[data-bag-ship-fill]');
    if (!fill) { if (foot) foot.__shipPct = 0; return; }
    const target = parseFloat(fill.dataset.target) || 0;
    const prev = foot && typeof foot.__shipPct === 'number' ? foot.__shipPct : target;
    fill.style.width = prev + '%';
    requestAnimationFrame(() => { fill.style.width = target + '%'; });
    if (foot) foot.__shipPct = target;
  };

  const post = (url, body) =>
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
      .then((r) => r.json());

  /* ---------- recommendation arrows: grey when inactive, black when active ---------- */
  const recRail = () => document.querySelector('[data-bag-recrail]');
  const updateRecArrows = () => {
    const rail = recRail();
    const prev = document.querySelector('[data-bag-rec-prev]');
    const next = document.querySelector('[data-bag-rec-next]');
    if (!rail || !prev || !next) return;
    const max = rail.scrollWidth - rail.clientWidth;
    prev.classList.toggle('is-active', rail.scrollLeft > 4);
    next.classList.toggle('is-active', rail.scrollLeft < max - 4);
  };
  const bindRecRail = () => {
    const rail = recRail();
    if (!rail || rail.__bound) return;
    rail.__bound = true;
    rail.addEventListener('scroll', () => window.requestAnimationFrame(updateRecArrows), { passive: true });
    updateRecArrows();
  };

  /* ---------- bag open / close ---------- */
  const openBag = () => { html.classList.add('s-bag-open'); bindRecRail(); requestAnimationFrame(updateRecArrows); };
  const closeBag = () => { html.classList.remove('s-bag-open'); };

  /* ---------- cart operations ---------- */
  const add = (items) =>
    post('/cart/add.js', { items, sections: SECTIONS, sections_url: location.pathname })
      .then((data) => { if (data && data.status) throw data; refreshBag(data); openBag(); return data; });

  /* Use the 1-based LINE INDEX: /cart/change.js only returns rendered sections
     for `line`, not for `id` (line key). We re-render after every op so the
     DOM order (and therefore the indices) stays in sync with the cart. */
  const change = (line, qty) =>
    post('/cart/change.js', { line: line, quantity: qty, sections: SECTIONS, sections_url: location.pathname }).then(refreshBag);

  window.SauvrynCart = { add, openBag, closeBag };

  const rowOf = (el) => el.closest('.s-bag__row');
  const lineOf = (el) => { const r = rowOf(el); return r ? parseInt(r.dataset.line, 10) : 0; };
  const qtyOf = (el) => { const r = rowOf(el); const n = r && r.querySelector('.s-bag__qtynum'); return n ? parseInt(n.textContent, 10) || 1 : 1; };

  /* ---------- QUICK VIEW ---------- */
  const initQV = (panel) => {
    if (panel.__qv) return;
    panel.__qv = true;
    let variants = [];
    try { variants = JSON.parse(panel.querySelector('[data-qa-variants]').textContent); } catch (e) { variants = []; }
    const colIdx = parseInt(panel.dataset.colourIndex, 10);
    const sizeIdx = parseInt(panel.dataset.sizeIndex, 10);
    const addBtn = panel.querySelector('[data-qa-add]');
    const sizeBtns = Array.from(panel.querySelectorAll('[data-qa-size]'));
    const colBtns = Array.from(panel.querySelectorAll('[data-qa-colour]'));
    const colName = panel.querySelector('[data-qa-colname]');
    const sizeLabel = panel.querySelector('[data-qa-sizelabel]');
    const sizeLabelDefault = sizeLabel ? sizeLabel.textContent.trim() : '';
    const T = { select: addBtn ? addBtn.textContent.trim() : '', add: S.add, adding: S.adding, added: S.added, sold: S.sold };
    let colour = colBtns.length ? colBtns[0].dataset.qaColour : null;
    let size = null;

    const variantFor = (c, z) => variants.find((v) => {
      const o = v.options || [];
      if (colIdx >= 0 && c != null && o[colIdx] !== c) return false;
      if (sizeIdx >= 0 && z != null && o[sizeIdx] !== z) return false;
      return true;
    });
    /* Sold-out sizes stay visible + selectable-for-feedback (muted strikethrough
       via .is-soldout, aria-disabled) rather than hard-disabled, so tapping one
       flips the add button to a disabled "Sold out". */
    const refreshSizes = () => sizeBtns.forEach((b) => {
      const v = variantFor(colour, b.dataset.qaSize);
      const ok = v && v.available;
      b.classList.toggle('is-soldout', !ok);
      b.setAttribute('aria-disabled', ok ? 'false' : 'true');
    });
    const anyAvailable = (c) => variants.some((v) => {
      if (colIdx >= 0 && c != null && (v.options || [])[colIdx] !== c) return false;
      return v.available;
    });
    const refreshAdd = () => {
      let sold = false, disabled = false, label = T.add;
      if (sizeIdx < 0) {
        const v = variantFor(colour, null) || variants[0];
        sold = !(v && v.available); disabled = sold; label = sold ? T.sold : T.add;
      } else if (!anyAvailable(colour)) {
        sold = true; disabled = true; label = T.sold;
      } else if (!size) {
        disabled = true; label = T.select;
      } else {
        const v = variantFor(colour, size);
        sold = !(v && v.available); disabled = sold; label = sold ? T.sold : T.add;
      }
      addBtn.disabled = disabled;
      addBtn.textContent = label;
      addBtn.classList.toggle('is-soldout', sold);
    };
    colBtns.forEach((b) => b.addEventListener('click', () => {
      colBtns.forEach((x) => x.classList.toggle('is-active', x === b));
      colour = b.dataset.qaColour;
      if (colName) colName.textContent = colour;
      if (size) { const v = variantFor(colour, size); if (!v || !v.available) { size = null; sizeBtns.forEach((s) => s.classList.remove('is-active')); if (sizeLabel) sizeLabel.textContent = sizeLabelDefault; } }
      refreshSizes(); refreshAdd();
    }));
    sizeBtns.forEach((b) => b.addEventListener('click', () => {
      if (b.disabled) return;
      size = b.dataset.qaSize;
      sizeBtns.forEach((x) => x.classList.toggle('is-active', x === b));
      if (sizeLabel) sizeLabel.textContent = (S.sizeWord || 'Size') + ': ' + size;
      refreshAdd();
    }));
    if (addBtn) addBtn.addEventListener('click', () => {
      const v = variantFor(colour, size) || (sizeIdx < 0 ? variants.find((x) => x.available) : null);
      if (!v || !v.available || addBtn.disabled) return;
      addBtn.disabled = true;
      addBtn.textContent = T.adding;
      add([{ id: v.id, quantity: 1 }])
        .then(() => { addBtn.textContent = T.added; setTimeout(() => { closeQV(panel); refreshAdd(); }, 700); })
        .catch(() => { addBtn.textContent = T.sold; setTimeout(refreshAdd, 1200); });
    });
    refreshSizes(); refreshAdd();
  };
  const openQV = (panel) => {
    initQV(panel);
    if (panel.parentElement !== document.body) document.body.appendChild(panel);
    const g = panel.querySelector('[data-qa-gallery]');
    if (g) g.scrollLeft = 0;
    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add('is-open'));
    html.classList.add('s-qa-open');
  };
  const closeQV = (panel) => {
    panel.classList.remove('is-open');
    html.classList.remove('s-qa-open');
    setTimeout(() => { panel.hidden = true; }, 300);
  };

  /* Swipe-down-to-close on the quick-view drag handle / bar. */
  let qvDrag = null, qvSheet = null, qvStartY = 0, qvDy = 0;
  document.addEventListener('pointerdown', (e) => {
    const grip = e.target.closest('[data-qa-handle]');
    if (!grip) return;
    const panel = e.target.closest('[data-qa]');
    if (!panel || !panel.classList.contains('is-open')) return;
    qvDrag = panel; qvSheet = panel.querySelector('.s-qa__sheet'); qvStartY = e.clientY; qvDy = 0;
    if (qvSheet) qvSheet.style.transition = 'none';
  });
  document.addEventListener('pointermove', (e) => {
    if (!qvDrag) return;
    qvDy = Math.max(0, e.clientY - qvStartY);
    if (qvSheet) qvSheet.style.transform = 'translateY(' + qvDy + 'px)';
  });
  const qvDragEnd = () => {
    if (!qvDrag) return;
    if (qvSheet) { qvSheet.style.transition = ''; qvSheet.style.transform = ''; }
    if (qvDy > 110) closeQV(qvDrag);
    qvDrag = null; qvSheet = null; qvDy = 0;
  };
  document.addEventListener('pointerup', qvDragEnd);
  document.addEventListener('pointercancel', qvDragEnd);

  /* ---------- delegated clicks ---------- */
  document.addEventListener('click', (e) => {
    /* quick view */
    const qaOpen = e.target.closest('[data-qa-open]');
    if (qaOpen) { e.preventDefault(); const p = document.getElementById(qaOpen.dataset.qaTarget); if (p) openQV(p); return; }
    const qaClose = e.target.closest('[data-qa-close]');
    if (qaClose) { const p = qaClose.closest('[data-qa]'); if (p) closeQV(p); return; }

    /* bag */
    const bagOpen = e.target.closest('[data-bag-open]');
    if (bagOpen) { e.preventDefault(); openBag(); return; }
    if (e.target.closest('[data-bag-close]')) { closeBag(); return; }
    const inc = e.target.closest('[data-bag-qty-inc]');
    if (inc) { change(lineOf(inc), qtyOf(inc) + 1); return; }
    const dec = e.target.closest('[data-bag-qty-dec]');
    if (dec) { change(lineOf(dec), Math.max(0, qtyOf(dec) - 1)); return; }
    const rem = e.target.closest('[data-bag-remove]');
    if (rem) { change(lineOf(rem), 0); return; }
    const prev = e.target.closest('[data-bag-rec-prev]');
    if (prev) { const r = document.querySelector('[data-bag-recrail]'); if (r) r.scrollBy({ left: -r.clientWidth * 0.85, behavior: 'smooth' }); return; }
    const next = e.target.closest('[data-bag-rec-next]');
    if (next) { const r = document.querySelector('[data-bag-recrail]'); if (r) r.scrollBy({ left: r.clientWidth * 0.85, behavior: 'smooth' }); return; }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const qv = document.querySelector('[data-qa].is-open');
    if (qv) { closeQV(qv); return; }
    if (html.classList.contains('s-bag-open')) closeBag();
  });

  bindRecRail();
  paintShipBar();
  window.addEventListener('resize', updateRecArrows, { passive: true });
})();
